/**
 * SOPStaffGrid.js
 * 
 * Reusable component: Staff (rows) × SOP (columns) grid with read/date toggle.
 * Used in: HeadOffice (SOP Compliance) and SinglePharmacy views.
 * 
 * Props:
 *   pharmacyId (string, required) - UUID of pharmacy
 *   asOfDate (ISO string, required) - Date for which to show assignment status
 *   showDates (boolean, optional) - Toggle between status mode and date mode
 *   onBack (function, optional) - Callback when user clicks back button
 *   masked (boolean, optional) - Apply name masking (for admin demo mode)
 */

import React, { useState, useEffect, useMemo, useRef } from "react";

// Helper: format ISO date to "DD MMM YYYY"
function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return "—";
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

// Helper: percentage to color (red → amber → green)
function pctColor(pct) {
  if (pct === null || pct === undefined) return "#f3f4f6";
  const hue = Math.max(0, Math.min(120, (pct / 100) * 120));
  return `hsl(${hue}, 75%, 82%)`;
}

function pctTextColor(pct) {
  if (pct === null || pct === undefined) return "#9ca3af";
  return pct < 50 ? "#7f1d1d" : pct < 80 ? "#78350f" : "#064e3b";
}

// Helper: apply name masking
function displayName(name, masked) {
  if (!masked || !name) return name;
  return name.replace(/[a-z]/g, "x").replace(/[A-Z]/g, "X");
}

export default function SOPStaffGrid({ pharmacyId, asOfDate, showDates = false, onBack, masked = false }) {
  const [gridData, setGridData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [localShowDates, setLocalShowDates] = useState(showDates);
  const tokenRef = useRef(null);

  useEffect(() => {
    tokenRef.current = localStorage.getItem("authToken");
  }, []);

  // Load grid data from backend
  useEffect(() => {
    const loadGrid = async () => {
      setLoading(true);
      setError("");
      try {
        const token = tokenRef.current || localStorage.getItem("authToken");
        if (!token) throw new Error("Not authenticated");

        const res = await fetch("https://api.pharmacegroup.co.uk/sop_compliance_grid", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          },
          body: JSON.stringify({
            asOfDate: asOfDate,
            groupBy: "pharmacy",
            pharmacyId: pharmacyId
          })
        });

        if (!res.ok) throw new Error((await res.text()) || `HTTP ${res.status}`);
        const json = await res.json();
        setGridData(json);
        // DEBUG
        console.log("SOPStaffGrid response:", json);
      } catch (e) {
        setError(e.message || String(e));
      } finally {
        setLoading(false);
      }
    };

    loadGrid();
  }, [pharmacyId, asOfDate]);

  // Extract data
  const sopTitles = gridData?.sopTitles || [];
  const sopMeta = gridData?.sopMeta || {};
  const groups = gridData?.groups || [];

  // Build category groupings
  const titlesByCategory = useMemo(() => {
    const map = new Map();
    for (const t of sopTitles) {
      const cat = sopMeta[t]?.category || "(uncategorised)";
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat).push(t);
    }
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [sopTitles, sopMeta]);

  // For detail grid mode, we expect exactly one group (the selected pharmacy)
  const group = groups.length > 0 ? groups[0] : null;
  const staffNames = group?.headers || [];
  const cells = group?.cells || {};

  // Render cell content based on mode
  function renderCell(sopTitle, staffName) {
    const cell = cells?.[sopTitle]?.[staffName];

    // No assignment
    if (!cell) {
      return { symbol: "—", bgColor: "#f3f4f6", textColor: "#9ca3af" };
    }

    if (localShowDates) {
      // Date mode: show read date (green) or last reminder date (red)
      if (cell.readAndSignedAt) {
        return {
          symbol: fmtDate(cell.readAndSignedAt),
          bgColor: "#dcfce7",
          textColor: "#15803d"
        };
      } else if (cell.lastReminderSentAt) {
        return {
          symbol: fmtDate(cell.lastReminderSentAt),
          bgColor: "#fee2e2",
          textColor: "#991b1b"
        };
      } else {
        return {
          symbol: "Unread",
          bgColor: "#fee2e2",
          textColor: "#991b1b"
        };
      }
    } else {
      // Status mode: tick/cross based on didRead vs shouldHaveRead
      const didRead = cell.didRead ?? 0;
      const shouldHaveRead = cell.shouldHaveRead ?? 0;
      
      // If didRead equals shouldHaveRead (all read), show tick
      // If didRead is 0 (none read), show cross
      // Otherwise show partial symbol
      if (shouldHaveRead === 0) {
        return { symbol: "—", bgColor: "#f3f4f6", textColor: "#9ca3af" };
      } else if (didRead >= shouldHaveRead) {
        return {
          symbol: "✓",
          bgColor: "#dcfce7",
          textColor: "#15803d"
        };
      } else if (didRead > 0) {
        return {
          symbol: "◐",
          bgColor: "#fef3c7",
          textColor: "#92400e"
        };
      } else {
        return {
          symbol: "✗",
          bgColor: "#fee2e2",
          textColor: "#991b1b"
        };
      }
    }
  }

  // Render category roll-up row
  function renderCategoryRow(category, titles) {
    let totalShould = 0,
      totalDid = 0;

    for (const staffName of staffNames) {
      for (const title of titles) {
        const c = cells?.[title]?.[staffName];
        if (c) {
          totalShould += c.shouldHaveRead || 0;
          totalDid += c.didRead || 0;
        }
      }
    }

    const pct = totalShould > 0 ? Math.round((totalDid / totalShould) * 1000) / 10 : null;

    return (
      <tr key={`cat-${category}`} style={{ background: "#fafafa" }}>
        <td
          style={{
            padding: "6px 10px",
            fontWeight: 600,
            color: "#374151",
            borderBottom: "1px solid #e5e7eb",
            background: "#fafafa",
            position: "sticky",
            left: 0,
            zIndex: 3
          }}
        >
          {category} <span style={{ fontSize: 10, color: "#9ca3af", fontWeight: 400 }}>(roll-up)</span>
        </td>
        {staffNames.map((staffName) => (
          <td
            key={`${category}-${staffName}`}
            style={{
              padding: "4px 6px",
              textAlign: "center",
              borderBottom: "1px solid #e5e7eb",
              background: pctColor(pct),
              color: pctTextColor(pct),
              fontWeight: 600,
              fontSize: 11,
              minWidth: 80
            }}
          >
            {pct === null ? "—" : `${pct}%`}
          </td>
        ))}
      </tr>
    );
  }

  return (
    <div style={{ padding: 16, background: "#f9fafb" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <div>
          {onBack && (
            <button
              onClick={onBack}
              style={{
                padding: "6px 12px",
                background: "#fff",
                border: "1px solid #d1d5db",
                borderRadius: 4,
                cursor: "pointer",
                fontSize: 13,
                color: "#374151",
                marginBottom: 8
              }}
            >
              ← Back
            </button>
          )}
          <h3 style={{ margin: "0 0 4px 0", color: "#111827", fontSize: 16 }}>
            {group?.label ? displayName(group.label, masked) : "Pharmacy"} — Staff SOP Compliance
          </h3>
          <div style={{ fontSize: 13, color: "#6b7280" }}>
            {staffNames.length} staff · {sopTitles.length} SOPs
            {group?.overall && ` · ${group.overall.didRead}/${group.overall.shouldHaveRead} read (${group.overall.pct}%)`}
          </div>
        </div>

        {/* Toggle button */}
        <button
          onClick={() => setLocalShowDates(!localShowDates)}
          style={{
            padding: "6px 12px",
            background: localShowDates ? "#3b82f6" : "#fff",
            color: localShowDates ? "#fff" : "#374151",
            border: "1px solid #d1d5db",
            borderRadius: 4,
            cursor: "pointer",
            fontSize: 13,
            fontWeight: 500
          }}
        >
          {localShowDates ? "Show Status" : "Show Dates"}
        </button>
      </div>

      {/* Error state */}
      {error && (
        <div
          style={{
            padding: 16,
            color: "#dc2626",
            background: "#fef2f2",
            border: "1px solid #fecaca",
            borderRadius: 6,
            marginBottom: 16
          }}
        >
          Error: {error}
        </div>
      )}

      {/* Loading state */}
      {loading && <div style={{ padding: 24, color: "#6b7280" }}>Loading grid…</div>}

      {/* Empty state */}
      {!loading && !error && (!group || staffNames.length === 0 || sopTitles.length === 0) && (
        <div
          style={{
            padding: 24,
            color: "#6b7280",
            textAlign: "center",
            background: "#fff",
            border: "1px solid #e5e7eb",
            borderRadius: 8
          }}
        >
          No SOP assignments found for this pharmacy.
        </div>
      )}

      {/* Grid table with horizontal scrolling */}
      {!loading && !error && group && (
        <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden" }}>
          <div style={{ 
            overflowX: "auto",
            overflowY: "visible",
            maxHeight: "70vh"
          }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: 12,
                minWidth: "fit-content"
              }}
            >
              <thead>
                <tr>
                  <th
                    style={{
                      padding: "6px 10px",
                      textAlign: "left",
                      borderBottom: "1px solid #e5e7eb",
                      background: "#fff",
                      fontWeight: 600,
                      color: "#374151",
                      position: "sticky",
                      left: 0,
                      zIndex: 4,
                      minWidth: 250
                    }}
                  >
                    SOP
                  </th>
                  {staffNames.map((staffName) => (
                    <th
                      key={staffName}
                      style={{
                        padding: "6px 8px",
                        textAlign: "center",
                        borderBottom: "1px solid #e5e7eb",
                        minWidth: 100,
                        fontWeight: 600,
                        color: "#374151",
                        background: "#f9fafb",
                        fontSize: 11
                      }}
                    >
                      {displayName(staffName, masked)}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {titlesByCategory.map(([category, titles]) => (
                  <React.Fragment key={category}>
                    {/* Category roll-up */}
                    {renderCategoryRow(category, titles)}

                    {/* Individual SOP rows */}
                    {titles.map((sopTitle) => (
                      <tr key={sopTitle}>
                        <td
                          style={{
                            padding: "5px 10px 5px 10px",
                            borderBottom: "1px solid #f3f4f6",
                            color: "#374151",
                            background: "#fff",
                            position: "sticky",
                            left: 0,
                            zIndex: 3,
                            fontSize: 12,
                            fontWeight: 500,
                            minWidth: 250
                          }}
                        >
                          {sopTitle}
                        </td>
                        {staffNames.map((staffName) => {
                          const { symbol, bgColor, textColor } = renderCell(sopTitle, staffName);
                          return (
                            <td
                              key={`${sopTitle}-${staffName}`}
                              style={{
                                padding: "6px 4px",
                                textAlign: "center",
                                borderBottom: "1px solid #f3f4f6",
                                background: bgColor,
                                color: textColor,
                                fontSize: localShowDates ? 10 : 14,
                                fontWeight: localShowDates ? 400 : 600,
                                minWidth: 100
                              }}
                            >
                              {symbol}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}